import { theme } from "antd";
import { useCallback, useEffect, useState } from "react";

const { useToken } = theme;

const VideoBackground = ({
	children,
	showVideo = true,
	showDarkOverlay = true,
	showBlueGradient = true,
	showTopWhiteGradient = true,
	showBottomWhiteGradient = true,
	showVerticalGradient = false,
	videoUrl:
		videoUrlProp = "https://bodhistreams-test.b-cdn.net/Static/bg_video_soft_negative.mp4",
	containerStyle = {},
	contentStyle = {},
	darkOverlayOpacity = null,
	topGradientHeight = "30%",
	bottomGradientHeight = "28%",
}) => {
	const { token } = useToken();
	const [cachedVideoUrl, setCachedVideoUrl] = useState(null);
	const [isVideoLoading, setIsVideoLoading] = useState(true);

	const videoUrl = videoUrlProp;

	// Cleanup function for blob URLs
	const cleanupBlobUrl = useCallback((url) => {
		if (url?.startsWith("blob:")) {
			URL.revokeObjectURL(url);
		}
	}, []);

	// Reset cached video when videoUrl changes (theme change)
	useEffect(() => {
		cleanupBlobUrl(cachedVideoUrl);
		setCachedVideoUrl(null);
		setIsVideoLoading(true);
	}, [videoUrl, cleanupBlobUrl]);

	// Cache the video once it's loaded
	useEffect(() => {
		if (!showVideo || !videoUrl) return;

		// Only fetch if we don't have a cached video
		if (cachedVideoUrl) return;

		const cacheVideo = async () => {
			try {
				// Check if Cache API is supported
				if (!("caches" in window)) {
					setCachedVideoUrl(videoUrl);
					setIsVideoLoading(false);
					return;
				}

				const cacheName = "bodhistreams-video-cache";
				const cache = await caches.open(cacheName);

				// Check if video is already cached
				const cachedResponse = await cache.match(videoUrl);

				if (cachedResponse) {
					// Video is already cached, create blob URL
					const blob = await cachedResponse.blob();
					const blobUrl = URL.createObjectURL(blob);
					setCachedVideoUrl(blobUrl);
					setIsVideoLoading(false);
				} else {
					// Video not cached, fetch and cache it
					const response = await fetch(videoUrl);

					if (response.ok) {
						// Cache the response
						await cache.put(videoUrl, response.clone());

						// Create blob URL for immediate use
						const blob = await response.blob();
						const blobUrl = URL.createObjectURL(blob);
						setCachedVideoUrl(blobUrl);
						setIsVideoLoading(false);
					} else {
						throw new Error("Failed to fetch video");
					}
				}
			} catch (_error) {
				// Fallback to original URL
				setCachedVideoUrl(videoUrl);
				setIsVideoLoading(false);
			}
		};

		cacheVideo();
	}, [videoUrl, showVideo, cachedVideoUrl]);

	// Cleanup blob URL when component unmounts
	useEffect(
		() => () => {
			cleanupBlobUrl(cachedVideoUrl);
		},
		[cachedVideoUrl, cleanupBlobUrl],
	);
	const defaultContainerStyle = {
		position: "relative",
		overflow: "hidden",
		...containerStyle,
	};

	const defaultContentStyle = {
		position: "relative",
		zIndex: 2,
		...contentStyle,
	};

	return (
		<div style={defaultContainerStyle}>
			{/* Video Background */}
			{showVideo && cachedVideoUrl && (
				<video
					autoPlay
					loop
					muted
					playsInline
					onLoadStart={() => setIsVideoLoading(false)}
					style={{
						position: "absolute",
						width: "100%",
						height: "100%",
						objectFit: "cover",
						top: 0,
						left: 0,
						zIndex: 0,
						opacity: isVideoLoading ? 0 : 1,
						transition: "opacity 0.3s ease-in-out",
					}}
				>
					<source src={cachedVideoUrl} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			)}

			{/* Loading placeholder while video is being cached */}
			{showVideo && isVideoLoading && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						backgroundColor: token.colorBlueRgbaLight,
						zIndex: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<div
						style={{
							color: "white",
							fontSize: "14px",
							opacity: 0.7,
						}}
					>
						Loading video...
					</div>
				</div>
			)}

			{/* Dark overlay for better text visibility */}
			{showDarkOverlay && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						backgroundColor: darkOverlayOpacity || token.darkOverlay,
						zIndex: 1,
						pointerEvents: "none",
					}}
				/>
			)}

			{/* Blue gradient overlay from top to bottom */}
			{showBlueGradient && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						background: token.colorBlueRgbaVideo,
						zIndex: 1,
						pointerEvents: "none",
					}}
				/>
			)}

			{/* Top white gradient overlay */}
			{showTopWhiteGradient && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: topGradientHeight,
						background: `linear-gradient(to top, ${token.whiteTransparent} 0%, ${token.whiteSolid} 100%)`,
						zIndex: 1,
						pointerEvents: "none",
					}}
				/>
			)}

			{/* Bottom white gradient overlay */}
			{showBottomWhiteGradient && (
				<div
					style={{
						position: "absolute",
						bottom: 0,
						left: 0,
						width: "100%",
						height: bottomGradientHeight,
						background: `linear-gradient(to bottom, ${token.whiteTransparent} 0%, ${token.whiteSolid} 100%)`,
						zIndex: 1,
						pointerEvents: "none",
					}}
				/>
			)}

			{/* Vertical gradient overlay - opaque at top and bottom, transparent in middle */}
			{showVerticalGradient && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						background: `linear-gradient(to bottom, ${token.colorBgLayout} 0%, transparent 30%, transparent 30%, ${token.colorBgLayout} 100%)`,
						zIndex: 1,
						pointerEvents: "none",
					}}
				/>
			)}

			{/* Content */}
			<div style={defaultContentStyle}>{children}</div>
		</div>
	);
};

export default VideoBackground;
